import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import { toast } from "sonner";

const fields = [
  { name: "name", label: "Distributor Name", placeholder: "ACME Distributors Pvt. Ltd." },
  { name: "gst_no", label: "GST Number", placeholder: "24ABCDE1234F1Z5" },
  { name: "phone", label: "Phone (10-digit)", placeholder: "9876543210" },
  { name: "email", label: "Email", placeholder: "owner@acme.com" },
  { name: "address_street", label: "Street Address", placeholder: "Plot 12, MIDC Industrial Area" },
  { name: "address_city", label: "City", placeholder: "Ahmedabad" },
  { name: "address_state", label: "State", placeholder: "Gujarat" },
  { name: "address_pin", label: "PIN Code", placeholder: "380015" },
];

export default function DistributorRegister() {
  const [form, setForm] = useState(Object.fromEntries(fields.map(f => [f.name, ""])));
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    for (const f of fields) {
      if (!form[f.name]) { toast.error(`${f.label} is required`); return; }
    }
    setLoading(true);
    try {
      await api.post("/auth/register-distributor", form);
      setDone(true);
      toast.success("Registration submitted!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] text-slate-900 flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-2xl border border-[#E5E7EB] bg-[#FFFFFF] p-8 text-center anim-up">
          <div className="h-14 w-14 mx-auto rounded-full bg-[#10B981]/10 border border-[#10B981]/30 flex items-center justify-center mb-4">
            <CheckCircle2 size={28} className="text-[#10B981]" />
          </div>
          <h2 className="text-2xl font-medium">Registration submitted</h2>
          <p className="mt-3 text-sm text-[#475569]">Your application is pending manufacturer approval. You'll receive a WhatsApp message once approved and can then log in with your mobile number.</p>
          <Link to="/" className="mt-6 inline-block px-5 py-2.5 bg-[#E63946] hover:bg-[#D32F2F] rounded-md text-sm" data-testid="back-home-success">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFFFF] text-slate-900">
      <header className="border-b border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Logo size="md" />
          <Link to="/" className="text-sm text-[#475569] hover:text-slate-900 inline-flex items-center gap-2" data-testid="reg-back">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E63946]/10 border border-[#E63946]/30 text-[11px] uppercase tracking-[0.18em] text-[#E63946] mb-3">
          <Building2 size={12} /> Distributor Registration
        </div>
        <h1 className="text-3xl lg:text-4xl tracking-tighter font-medium">Apply to become a Chiripal Distributor</h1>
        <p className="text-[#475569] mt-2 max-w-2xl">
          Submit your business details. The manufacturer will review and approve your account, after which you can sign in via WhatsApp OTP and manage your dealer network.
        </p>

        <form onSubmit={submit} className="mt-8 rounded-2xl border border-[#E5E7EB] bg-[#FFFFFF] p-6 sm:p-8">
          <div className="grid sm:grid-cols-2 gap-5">
            {fields.map((f) => (
              <div key={f.name} className={f.name === "address_street" ? "sm:col-span-2" : ""}>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">{f.label}</label>
                <input
                  data-testid={`reg-${f.name}`}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  placeholder={f.placeholder}
                  className="mt-1 w-full px-3 py-2.5 bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900 focus:outline-none focus:border-[#E63946]"
                />
              </div>
            ))}
          </div>
          <button
            data-testid="register-submit"
            type="submit" disabled={loading}
            className="mt-8 w-full sm:w-auto px-6 py-3 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded-md font-medium disabled:opacity-60"
          >
            {loading ? "Submitting..." : "Submit Registration"}
          </button>
        </form>
      </div>
    </div>
  );
}
